import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Template } from './templatesApi'

const inputClass = 'bg-black border border-white/20 rounded outline-none px-2 py-1 text-xs'

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

const pad = (n: number) => String(n).padStart(2, '0')

type Row = { profile_id: number; count: number }

/**
 * "N of this template", repeated.
 *
 * A window used to carry one profile and a count, so an event needing two Duos
 * and a Solos took two schedules with identical times -- and changing one and
 * forgetting the other is the mistake that follows.
 */
export const TemplatePicker = ({
    rows,
    templates,
    onChange,
}: {
    rows: Row[]
    templates: Template[]
    onChange: (rows: Row[]) => void
}) => {
    const used = new Set(rows.map((r) => r.profile_id))
    const unused = templates.filter((t) => !used.has(t.id))
    const total = rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0)

    const update = (index: number, patch: Partial<Row>) =>
        onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))

    return (
        <div className='flex flex-col gap-2'>
            <span className='text-[10px] uppercase tracking-widest text-white/50'>
                deploys {total} server{total === 1 ? '' : 's'} in total
            </span>
            {rows.map((row, index) => (
                <div key={index} className='flex flex-row items-center gap-2'>
                    <input
                        type='number'
                        min={1}
                        max={20}
                        value={row.count}
                        onChange={(e) => update(index, { count: Number(e.target.value) || 1 })}
                        className={`${inputClass} w-16`}
                    />
                    <span className='text-white/40 text-xs'>×</span>
                    <select
                        value={row.profile_id}
                        onChange={(e) => update(index, { profile_id: Number(e.target.value) })}
                        className={`${inputClass} min-w-[12rem]`}
                    >
                        {templates.map((t) => (
                            <option
                                key={t.id}
                                value={t.id}
                                className='bg-neutral-900'
                                // Already on another row: saving it would collide with the
                                // one-row-per-template constraint, so it cannot be picked twice.
                                disabled={t.id !== row.profile_id && used.has(t.id)}
                            >
                                {t.name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => onChange(rows.filter((_, i) => i !== index))}
                        // The last row cannot go: the server refuses a schedule with no
                        // template, so stop one being built here.
                        disabled={rows.length === 1}
                        className='text-red-400 disabled:opacity-30 px-1'
                        title={rows.length === 1 ? 'A schedule needs at least one template' : 'Remove'}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            ))}
            {unused.length > 0 && (
                <button
                    onClick={() => onChange([...rows, { profile_id: unused[0].id, count: 1 }])}
                    className='self-start text-[11px] uppercase tracking-wide text-amber-400 hover:underline'
                >
                    <FontAwesomeIcon icon={faPlus} className='mr-1' /> add another template
                </button>
            )}
        </div>
    )
}

/**
 * When the schedule runs, and for how long.
 *
 * Date and time are separate inputs because they are separate decisions -- the
 * day is usually settled before the hour -- and one combined `datetime-local`
 * is awkward to type into. The readout beside each states the interpreted
 * result in UTC, which is the check that catches a schedule set to the wrong
 * hour before anyone relies on it.
 */
export const WhenBox = ({
    deployAt,
    destroyAt,
    onChange,
}: {
    deployAt: string
    destroyAt: string
    onChange: (next: { deploy_at?: string; destroy_at?: string }) => void
}) => {
    const split = (value: string) => (value ? value.split('T') : ['', ''])
    const [deployDate, deployTime] = split(deployAt)
    const [destroyDate, destroyTime] = split(destroyAt)

    const join = (date: string, time: string) => (date && time ? `${date}T${time}` : '')

    const localNow = (offsetMinutes = 0) => {
        const d = new Date(Date.now() + offsetMinutes * 60000)
        return [
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
            `${pad(d.getHours())}:${pad(d.getMinutes())}`,
        ]
    }

    const preset = (label: string, startsIn: number, lasts: number) => (
        <button
            key={label}
            onClick={() => {
                const [d, t] = localNow(startsIn)
                const [d2, t2] = localNow(startsIn + lasts)
                onChange({ deploy_at: join(d, t), destroy_at: join(d2, t2) })
            }}
            className='border border-white/20 rounded px-2 py-1 text-[10px] uppercase tracking-wide hover:bg-white/10'
        >
            {label}
        </button>
    )

    const asUtc = (value: string) => {
        if (!value) return null
        const d = new Date(value)
        return isNaN(d.getTime()) ? null : `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`
    }

    const span = (() => {
        if (!deployAt || !destroyAt) return null
        const minutes = (new Date(destroyAt).getTime() - new Date(deployAt).getTime()) / 60000
        if (isNaN(minutes)) return null
        if (minutes <= 0) return { text: 'teardown is before the deployment', bad: true }
        const hours = Math.floor(minutes / 60)
        return {
            text: `servers stay up for ${hours ? `${hours}h ` : ''}${Math.round(minutes % 60)}m`,
            bad: false,
        }
    })()

    const row = (
        label: string,
        date: string,
        time: string,
        key: 'deploy_at' | 'destroy_at',
        optional = false
    ) => (
        <div className='flex flex-col gap-1'>
            <span className='text-[10px] uppercase tracking-widest text-white/50'>
                {label} <span className='text-white/30'>({BROWSER_TIMEZONE})</span>
            </span>
            <div className='flex flex-row items-center gap-2 flex-wrap'>
                <input
                    type='date'
                    value={date}
                    onChange={(e) => onChange({ [key]: join(e.target.value, time || '00:00') })}
                    className={`${inputClass} [&::-webkit-calendar-picker-indicator]:invert`}
                />
                <input
                    type='time'
                    value={time}
                    onChange={(e) => onChange({ [key]: join(date, e.target.value) })}
                    className={`${inputClass} [&::-webkit-calendar-picker-indicator]:invert`}
                />
                {optional && (date || time) && (
                    <button
                        onClick={() => onChange({ [key]: '' })}
                        className='text-white/40 hover:text-red-400 text-[10px] uppercase'
                    >
                        clear
                    </button>
                )}
                <span className='text-[10px] text-white/40'>
                    {asUtc(join(date, time)) ?? (optional ? 'never torn down' : 'not set')}
                </span>
            </div>
        </div>
    )

    return (
        <div className='flex flex-col gap-3 border border-white/10 rounded p-3'>
            <div className='flex flex-row flex-wrap gap-2 items-center'>
                <span className='text-[10px] uppercase tracking-widest text-white/50'>quick set</span>
                {preset('in 1h, for 2h', 60, 120)}
                {preset('in 1h, for 4h', 60, 240)}
                {preset('in 24h, for 3h', 24 * 60, 180)}
            </div>
            {row('deploy at', deployDate, deployTime, 'deploy_at')}
            {row('destroy at', destroyDate, destroyTime, 'destroy_at', true)}
            {span && (
                <div className={span.bad ? 'text-[11px] text-red-400' : 'text-[11px] text-white/50'}>
                    {span.text}
                </div>
            )}
        </div>
    )
}
